import {
  Component,
  InjectionToken,
  NgZone,
  OnDestroy,
  OnInit,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { take } from 'rxjs/operators';
import {
  AiAssistantChatBottomsheetComponent
} from '@shared/bottom-sheet-layouts/ai-assistant-chat-bottomsheet/ai-assistant-chat-bottomsheet.component';
import { EventLoggerService } from '@services/event-logger-service';
import { BroadcastService } from '@services/broadcast-service';

const STORAGE_KEY = 'aiChatFabPosition';
const EDGE_MARGIN = 4;
const FAB_SIZE = 40;
const DRAG_THRESHOLD = 6;
const DEFAULT_BOTTOM_OFFSET = 72;

export const AI_CHAT_FAB_OPEN_DELAY_MS = new InjectionToken<number>('AI_CHAT_FAB_OPEN_DELAY_MS', {
  factory: (): number => 50,
});

interface FabPosition {
  x: number;
  y: number;
}

@Component({
  selector: 'app-ai-chat-fab',
  templateUrl: './ai-chat-fab.component.html',
})
export class AiChatFabComponent implements OnInit, OnDestroy {
  readonly user = input<Table.User>();

  readonly posX = signal<number>(0);
  readonly posY = signal<number>(0);
  readonly snapping = signal<boolean>(false);

  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly eventLogger = inject(EventLoggerService);
  private readonly broadcast = inject(BroadcastService);
  private readonly zone = inject(NgZone);
  private readonly openDelayMs = inject(AI_CHAT_FAB_OPEN_DELAY_MS);

  private dragging: boolean = false;
  private moved: boolean = false;
  private pointerId: number | null = null;
  private offsetX: number = 0;
  private offsetY: number = 0;
  private readonly onMove = (event: PointerEvent): void => this.handleMove(event);
  private readonly onUp = (event: PointerEvent): void => this.handleUp(event);

  ngOnInit(): void {
    this.restorePosition();
  }

  ngOnDestroy(): void {
    this.detachDragListeners();
  }

  onPointerDown(event: PointerEvent): void {
    this.dragging = true;
    this.moved = false;
    this.snapping.set(false);
    this.pointerId = event.pointerId;

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.offsetX = event.clientX - rect.left;
    this.offsetY = event.clientY - rect.top;

    this.zone.runOutsideAngular(() => {
      document.addEventListener('pointermove', this.onMove, { passive: false });
      document.addEventListener('pointerup', this.onUp);
      document.addEventListener('pointercancel', this.onUp);
    });
  }

  private handleMove(event: PointerEvent): void {
    if (!this.dragging || event.pointerId !== this.pointerId) return;
    event.preventDefault();

    const nextX = event.clientX - this.offsetX;
    const nextY = event.clientY - this.offsetY;
    if (Math.abs(nextX - this.posX()) > DRAG_THRESHOLD || Math.abs(nextY - this.posY()) > DRAG_THRESHOLD) {
      this.moved = true;
    }
    this.zone.run(() => {
      this.posX.set(this.clampX(nextX));
      this.posY.set(this.clampY(nextY));
    });
  }

  private handleUp(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) return;
    this.dragging = false;
    this.pointerId = null;
    this.detachDragListeners();

    this.zone.run(() => {
      if (this.moved) {
        this.snapToNearestEdge();
        this.persistPosition();
      } else {
        this.openChat();
      }
    });
  }

  private snapToNearestEdge(): void {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const distanceToLeft = this.posX();
    const distanceToRight = viewportWidth - this.posX() - FAB_SIZE;
    const distanceToTop = this.posY();
    const distanceToBottom = viewportHeight - this.posY() - FAB_SIZE;
    const minHorizontal = Math.min(distanceToLeft, distanceToRight);
    const minVertical = Math.min(distanceToTop, distanceToBottom);

    this.snapping.set(true);
    if (minHorizontal <= minVertical) {
      this.posX.set(distanceToLeft <= distanceToRight ? EDGE_MARGIN : viewportWidth - FAB_SIZE - EDGE_MARGIN);
    } else {
      this.posY.set(distanceToTop <= distanceToBottom ? EDGE_MARGIN : viewportHeight - FAB_SIZE - EDGE_MARGIN);
    }
  }

  private openChat(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'paid_ai_chat_fab_click' }));

    setTimeout(() => {
      const sheetRef = this.bottomSheet.open(AiAssistantChatBottomsheetComponent, {
        data: { user: this.user() },
        disableClose: true,
        panelClass: 'bottomsheet-fixed-height-85vh',
      });

      sheetRef.backdropClick().pipe(take(1)).subscribe((): void => {
        this.broadcast.broadcast('KEYBOARD_VISIBLE', false);
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && ['INPUT', 'TEXTAREA'].includes(activeElement.tagName)) activeElement.blur();
        sheetRef.dismiss();
      });
    }, this.openDelayMs);
  }

  private clampX(x: number): number {
    return Math.min(Math.max(x, EDGE_MARGIN), window.innerWidth - FAB_SIZE - EDGE_MARGIN);
  }

  private clampY(y: number): number {
    return Math.min(Math.max(y, EDGE_MARGIN), window.innerHeight - FAB_SIZE - EDGE_MARGIN);
  }

  private restorePosition(): void {
    const stored = this.readStoredPosition();
    if (stored) {
      this.posX.set(this.clampX(stored.x));
      this.posY.set(this.clampY(stored.y));
      return;
    }
    this.posX.set(window.innerWidth - FAB_SIZE - EDGE_MARGIN);
    this.posY.set(window.innerHeight - FAB_SIZE - EDGE_MARGIN - DEFAULT_BOTTOM_OFFSET);
  }

  private readStoredPosition(): FabPosition | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) as FabPosition : null;
    } catch {
      return null;
    }
  }

  private persistPosition(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ x: this.posX(), y: this.posY() }));
    } catch {
      return;
    }
  }

  private detachDragListeners(): void {
    document.removeEventListener('pointermove', this.onMove);
    document.removeEventListener('pointerup', this.onUp);
    document.removeEventListener('pointercancel', this.onUp);
  }
}
